import { createContext, useContext, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export interface MetaData {
  title?: string;
  description?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImage?: string;
  metaUrl?: string;
  metaType?: string;
  prerenderStatusCode?: "200" | "404" | "410";
}

const defaultMeta: MetaData = {
  title: "Zara Preowned",
  description:
    "Buy and sell your Zara's used clothes, shoes, and accessories. Join our community to find great deals and give your pre-loved Zara items a new home.",
  metaTitle: undefined,
  metaDescription: undefined,
  metaImage: undefined,
  metaUrl: undefined,
  metaType: undefined,
  prerenderStatusCode: undefined,
};

const MetaContext = createContext<{
  meta: MetaData;
  setMeta: React.Dispatch<React.SetStateAction<MetaData>>;
} | null>(null);

export const MetaProvider = ({ children }: { children: React.ReactNode }) => {
  const [meta, setMeta] = useState<MetaData>(defaultMeta);
  const location = useLocation();

  // Reiniciar metaetiquetas a valores por defecto en cada cambio de ruta
  useEffect(() => {
    setMeta(defaultMeta);
  }, [location.pathname]);

  return (
    <MetaContext.Provider value={{ meta, setMeta }}>
      <Helmet prioritizeSeoTags>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && (
          <meta name="description" content={meta.description} />
        )}

        {/* Metaetiquetas Open Graph */}
        {meta.metaTitle && (
          <meta property="og:title" content={meta.metaTitle} />
        )}
        {meta.metaDescription && (
          <meta property="og:description" content={meta.metaDescription} />
        )}
        {meta.metaImage && (
          <meta property="og:image" content={meta.metaImage} />
        )}
        {meta.metaUrl && <meta property="og:url" content={meta.metaUrl} />}
        {meta.metaType && <meta property="og:type" content={meta.metaType} />}
        {(meta.metaTitle ||
          meta.metaDescription ||
          meta.metaImage ||
          meta.metaUrl ||
          meta.metaType) && (
          <meta property="og:site_name" content="Zara Preowned" />
        )}

        {/* Metaetiquetas de Twitter Card */}
        {meta.metaTitle && (
          <meta name="twitter:title" content={meta.metaTitle} />
        )}
        {meta.metaDescription && (
          <meta name="twitter:description" content={meta.metaDescription} />
        )}
        {meta.metaUrl && <meta name="twitter:url" content={meta.metaUrl} />}
        {meta.metaImage && (
          <meta name="twitter:image" content={meta.metaImage} />
        )}
        {(meta.metaTitle ||
          meta.metaDescription ||
          meta.metaImage ||
          meta.metaUrl) && (
          <meta name="twitter:card" content="summary-large-image" />
        )}
        {meta.prerenderStatusCode && (
          <meta
            name="prerender-status-code"
            content={meta.prerenderStatusCode}
          />
        )}
      </Helmet>
      {children}
    </MetaContext.Provider>
  );
};

export const useMetaContext = () => {
  const ctx = useContext(MetaContext);
  if (!ctx) {
    throw new Error("useMetaContext must be used within a MetaProvider");
  }
  return ctx;
};
